import { interactiveKinds, sceneAuthorable, sceneKeyColumn, sceneRole, } from './content.js';
/**
 * Changing which mechanic an existing question uses — spec 013 §12, `005` §3.
 *
 * Why this is a route of its own rather than a `kind` on the scene PATCH, and why the
 * answer key is always cleared while the labels always carry, are in
 * `docs/decisions/switching-a-mechanic.md`. The one fact to know while reading this file:
 * `SceneItem`'s `num_nonnulls(…) <= 1` CHECK allows one key column at a time, so a key
 * left behind does not fail here — it fails the next time somebody keys the item under
 * the new mechanic, naming a column they never touched.
 */
/** Every column on `Scene` that a mechanic reads. */
export const MECHANIC_COLUMNS = [
    'maxSelections',
    'requireSubmit',
    'min',
    'max',
    'step',
    'unit',
    'targetMin',
    'targetMax',
    'poolTotal',
    'counterpartBrief',
];
/**
 * Which of those columns each kind reads.
 *
 * A `Record<SceneKind, …>` for the reason `sceneKeyColumn` is one: a thirteenth kind has
 * to declare what it reads, where a hand-kept list would let it inherit the last
 * mechanic's track and render a slider nobody asked for.
 */
export const sceneMechanicColumns = {
    HOOK: [],
    CONCEPT: [],
    CASE: [],
    ARTICLE: [],
    RECAP: [],
    REWARD: [],
    // `requireSubmit` belongs to whichever kinds `sceneCommitsOnSelect` marks, which is
    // CHOICE alone. On any other mechanic it is a flag asking for a control that is
    // already there, so a switch clears it rather than leaving it to mean nothing.
    CHOICE: ['maxSelections', 'requireSubmit'],
    RANK: [],
    BUCKET: [],
    SLIDER: ['min', 'max', 'step', 'unit', 'targetMin', 'targetMax'],
    // `unit` is the slider's too, so a slider becoming a split keeps what it counted in.
    ALLOCATE: ['poolTotal', 'unit'],
    SPEAK: ['counterpartBrief'],
};
/**
 * What a scene becoming `to` must have nulled.
 *
 * By subtraction rather than a per-pair table: the columns a mechanic does not read are
 * exactly the ones that would otherwise survive as a value no screen shows and no gate
 * checks. `defined()` strips `undefined` on the way to Prisma, so a column left out of
 * the write keeps its old value — which is why this returns the list to null explicitly.
 */
export function clearedColumns(to) {
    const kept = new Set(sceneMechanicColumns[to]);
    return MECHANIC_COLUMNS.filter((column) => !kept.has(column));
}
/**
 * What the reviewer still owes once the scene is this kind, named as the fields the
 * console already labels in `FIELD_NAMES`.
 *
 * One entry per `part([…])` path `sceneIssueParts` can raise against a scene whose
 * mechanic has just changed — every item unkeyed, every mechanic column null.
 * `mechanic.test.ts` asserts the two agree, because this is the sentence a reviewer reads
 * *before* deciding, and the gate is what they meet after: a list that over-states sends
 * them to fill fields nothing wants, and one that under-states makes the change look free.
 *
 * The prose stays in `publish-rules` — two wordings of one obligation drift.
 */
const NEEDS = {
    HOOK: [],
    CONCEPT: [],
    CASE: [],
    ARTICLE: [],
    RECAP: [],
    REWARD: [],
    CHOICE: ['items'],
    RANK: ['items'],
    BUCKET: ['buckets', 'items'],
    SLIDER: ['min', 'max', 'step', 'targetMin', 'targetMax'],
    ALLOCATE: ['poolTotal', 'items'],
    SPEAK: [],
};
/**
 * The mechanics this question can become — the list the console shows, in enum order.
 *
 * Empty for a narrative beat, which has no mechanic: a Concept turned into a Slider loses
 * the body it was written for, and that is a rewrite rather than a change of mechanic.
 * The scene's current kind is absent because switching to it is not a change.
 *
 * `itemCount` and not the items: the only question asked of them is whether there are
 * any, and passing the array would invite a caller to think their order matters here.
 */
export function mechanicSwitches(from, itemCount) {
    if (sceneRole[from] !== 'interactive')
        return [];
    // Narrowed through `sceneAuthorable` — GEN-91. Every interactive kind is authorable
    // today, so the set is unchanged; reading the table rather than assuming that is what
    // keeps a returned kind one the write endpoint will accept if the two ever part.
    return interactiveKinds
        .filter((kind) => kind !== from && sceneAuthorable[kind])
        .map((kind) => ({
        kind,
        // Derived rather than listed: `sceneKeyColumn` is non-null for exactly the four
        // mechanics that hold items, so a thirteenth joins both at once. A hand-kept list
        // would have told the reviewer their items were dropped when they were not.
        usesItems: itemCount > 0 && sceneKeyColumn[kind] !== null,
        needs: NEEDS[kind],
    }));
}
//# sourceMappingURL=mechanic.js.map